import { useLocation } from 'react-router-dom';
import { SEO } from './SEO';
import { NodeGraphBackground } from './NodeGraphBackground';
import { SITE_CONFIG } from '@constants';

interface PageHeroProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  seoTitle?: string;
  seoDescription?: string;
  /** Override the default hero background image (e.g. a per-post blog image). */
  backgroundImage?: string;
  /** Alt text for a custom background image. */
  backgroundAlt?: string;
  /** Override the og:image / twitter:image used for social sharing. */
  seoImage?: string;
}

export function PageHero({
  title,
  subtitle,
  eyebrow,
  seoTitle,
  seoDescription,
  backgroundImage,
  backgroundAlt,
  seoImage,
}: PageHeroProps) {
  const location = useLocation();
  const canonicalUrl = `${SITE_CONFIG.url}${location.pathname}`;

  return (
    <>
      {seoTitle && (
        <SEO
          title={seoTitle}
          description={seoDescription || title}
          url={canonicalUrl}
          image={seoImage}
          type={seoImage ? 'article' : undefined}
        />
      )}

      <section className="relative py-24 bg-primary overflow-hidden h-[500px] flex items-center">
        {/* A custom background image (e.g. a blog post) sits behind a scrim;
            otherwise fall back to the brand node-network background. */}
        {backgroundImage ? (
          <div className="absolute inset-0">
            <img
              src={backgroundImage}
              alt={backgroundAlt || title}
              fetchPriority="high"
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/70 to-primary/40" />
          </div>
        ) : (
          <NodeGraphBackground variant="page" />
        )}

        <div className="container-custom relative z-10 pt-16">
          {eyebrow && (
            <span className="text-sm font-medium text-teal tracking-wide uppercase">{eyebrow}</span>
          )}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-primary-foreground mt-4">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg text-primary-foreground/70 mt-4 max-w-3xl">{subtitle}</p>
          )}
        </div>
      </section>
    </>
  );
}
